import { useState } from "react";
import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";

const stats = [
  { value: "20万+", label: "精选视频内容" },
  { value: "6 大类", label: "内容分类频道" },
  { value: "1080P", label: "高清观看体验" },
  { value: "1000万+", label: "月活跃观看用户" },
];

const values = [
  {
    icon: "ri-hd-line",
    title: "内容清晰",
    desc: "坚持高清与规范呈现，让每一帧画面都值得观看。",
  },
  {
    icon: "ri-compass-3-line",
    title: "浏览高效",
    desc: "清晰的分类与多维筛选，帮助用户用最短时间找到想看的内容。",
  },
  {
    icon: "ri-heart-3-line",
    title: "体验为本",
    desc: "PC 与移动端一致的顺滑体验，随时打开随时观看。",
  },
];

const milestones = [
  { year: "2020", text: "一起草视频正式上线，首批上线影视与短视频频道。" },
  { year: "2022", text: "内容分类体系全面升级，新增综艺、动漫与纪录片频道。" },
  { year: "2024", text: "完成移动端适配改版，推出一体化多维筛选与搜索体验。" },
  { year: "2026", text: "内容库突破 20 万条，成为多元高清视频内容的一站式入口。" },
];

export default function AboutPage() {
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  usePageMeta({
    title: "关于我们 - 一起草视频",
    description:
      "了解一起草视频的品牌定位、发展历程与联系方式。我们专注为影视、娱乐与短视频爱好者提供高清、清晰的观看体验。",
    keywords: "一起草视频,关于我们,视频平台,联系方式,高清视频",
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("company_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    setStatus("submitting");
    setFormError("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "company_alt") return;
      payload.append(key, String(value));
    });

    try {
      const response = await fetch("https://readdy.ai/api/form/db8hkts6oi3g3tv8und0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg ? serverMsg : "提交失败，请稍后重试。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <div className="bg-background-50">
      <section className="bg-primary-950">
        <div className="px-4 md:px-6 py-12 md:py-20">
          <div className="max-w-3xl">
            <span className="inline-block px-3 py-1 rounded-md bg-accent-500 text-primary-950 text-xs font-bold mb-4">
              关于我们
            </span>
            <h1 className="text-2xl md:text-4xl font-extrabold text-background-50 leading-tight">
              让每一段视频，都值得被认真观看
            </h1>
            <p className="mt-5 text-sm md:text-base text-primary-100/85 leading-relaxed">
              一起草视频是一个专注于高清视频分享与在线观看的内容平台。我们汇聚影视剧、娱乐节目、综艺、动漫与短视频等多元内容，
              通过清晰的分类、多维的筛选与高效的浏览体验，帮助每一位用户在感兴趣的内容里，找到属于自己的那一份快乐。
            </p>
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6 py-10 md:py-14">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {stats.map((s) => (
            <div key={s.label} className="p-5 md:p-6 rounded-lg bg-background-100 border border-background-200">
              <p className="text-2xl md:text-3xl font-extrabold text-primary-600">{s.value}</p>
              <p className="mt-1.5 text-xs md:text-sm text-foreground-500">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-background-100 border-y border-background-200">
        <div className="px-4 md:px-6 py-10 md:py-14">
          <h2 className="text-lg md:text-2xl font-bold text-foreground-950 mb-2">我们的坚持</h2>
          <p className="text-sm md:text-base text-foreground-500 mb-8 max-w-2xl">
            内容、效率与体验，是我们做每一个决定时的三条准绳。
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {values.map((v) => (
              <div key={v.title} className="p-5 md:p-6 rounded-lg bg-background-50 border border-background-200">
                <span className="w-11 h-11 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center mb-4">
                  <i className={`${v.icon} text-2xl`} />
                </span>
                <h3 className="text-base font-semibold text-foreground-950">{v.title}</h3>
                <p className="mt-2 text-sm text-foreground-500 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6 py-10 md:py-14">
        <h2 className="text-lg md:text-2xl font-bold text-foreground-950 mb-2">发展历程</h2>
        <p className="text-sm md:text-base text-foreground-500 mb-8 max-w-2xl">
          从上线至今，一起草视频一直在为更好的观看体验而迭代。
        </p>
        <ol className="relative border-l-2 border-background-200 ml-2 space-y-8">
          {milestones.map((m) => (
            <li key={m.year} className="pl-6 relative">
              <span className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-accent-500 border-4 border-background-50" />
              <p className="text-sm font-bold text-primary-600">{m.year}</p>
              <p className="mt-1 text-sm md:text-base text-foreground-700 leading-relaxed">{m.text}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="bg-background-100 border-y border-background-200">
        <div className="px-4 md:px-6 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            <div>
              <h2 className="text-lg md:text-2xl font-bold text-foreground-950 mb-2">联系我们</h2>
              <p className="text-sm md:text-base text-foreground-500 leading-relaxed mb-6">
                无论是内容合作、版权问题还是使用反馈，都欢迎给我们留言。我们会认真阅读每一条信息，并在工作日尽快回复。
              </p>
              <ul className="space-y-4 text-sm">
                <li className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center shrink-0">
                    <i className="ri-mail-line text-base" />
                  </span>
                  <div>
                    <p className="font-medium text-foreground-900">邮箱</p>
                    <p className="text-foreground-500">hello@yiqicaoshipin.com</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center shrink-0">
                    <i className="ri-phone-line text-base" />
                  </span>
                  <div>
                    <p className="font-medium text-foreground-900">客服电话</p>
                    <p className="text-foreground-500">400-800-1234（工作日 9:00-18:00）</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center shrink-0">
                    <i className="ri-map-pin-line text-base" />
                  </span>
                  <div>
                    <p className="font-medium text-foreground-900">服务范围</p>
                    <p className="text-foreground-500">线上内容平台 · 面向全国用户</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="p-5 md:p-7 rounded-xl bg-background-50 border border-background-200">
              <h3 className="text-base md:text-lg font-bold text-foreground-950 mb-5">在线留言</h3>
              <form
                id="about-contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-4"
              >
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    姓名 <span className="text-accent-600">*</span>
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入你的称呼"
                    className="w-full h-11 px-3.5 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    邮箱 <span className="text-accent-600">*</span>
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="方便回复的联系邮箱"
                    className="w-full h-11 px-3.5 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition"
                  />
                </div>
                <div>
                  <label htmlFor="contact-topic" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    咨询类型
                  </label>
                  <select
                    id="contact-topic"
                    name="topic"
                    defaultValue="内容合作"
                    className="w-full h-11 px-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition cursor-pointer"
                  >
                    <option value="内容合作">内容合作</option>
                    <option value="版权问题">版权问题</option>
                    <option value="使用反馈">使用反馈</option>
                    <option value="其他">其他</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                    留言内容 <span className="text-accent-600">*</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    placeholder="请描述你遇到的问题或合作意向（最多 500 字）"
                    className="w-full px-3.5 py-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition resize-none"
                  />
                </div>

                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="form-company-alt"
                />

                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="w-full h-11 rounded-md bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === "submitting" ? "提交中…" : "提交留言"}
                </button>

                {status === "success" && (
                  <p className="flex items-center gap-2 text-sm text-primary-600">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-checkbox-circle-line text-base" />
                    </span>
                    提交成功，感谢你的留言，我们会尽快回复。
                  </p>
                )}
                {status === "error" && (
                  <p className="flex items-center gap-2 text-sm text-accent-700">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-error-warning-line text-base" />
                    </span>
                    {formError}
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6 py-10 md:py-14">
        <h2 className="text-lg md:text-2xl font-bold text-foreground-950 mb-6">法律声明与用户须知</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <div className="p-5 md:p-6 rounded-lg bg-background-100 border border-background-200">
            <h3 className="text-base font-semibold text-foreground-950 mb-3">内容说明</h3>
            <p className="text-sm text-foreground-500 leading-relaxed">
              本站展示的视频信息与封面均为演示内容，不提供真实视频播放与下载服务。所有内容仅用于产品展示与体验，不代表任何真实作品。
            </p>
          </div>
          <div className="p-5 md:p-6 rounded-lg bg-background-100 border border-background-200">
            <h3 className="text-base font-semibold text-foreground-950 mb-3">用户须知</h3>
            <p className="text-sm text-foreground-500 leading-relaxed">
              用户在使用本站时，应遵守相关法律法规，不得利用本站发布或传播违法违规信息。本站保留对不当内容与行为进行处理的权利。
            </p>
          </div>
          <div className="p-5 md:p-6 rounded-lg bg-background-100 border border-background-200">
            <h3 className="text-base font-semibold text-foreground-950 mb-3">知识产权</h3>
            <p className="text-sm text-foreground-500 leading-relaxed">
              本网站的整体设计、文字说明与标识归一起草视频所有。任何未经授权的复制、转载或商业使用均被禁止。
            </p>
          </div>
          <div className="p-5 md:p-6 rounded-lg bg-background-100 border border-background-200">
            <h3 className="text-base font-semibold text-foreground-950 mb-3">免责声明</h3>
            <p className="text-sm text-foreground-500 leading-relaxed">
              本站不对演示数据的准确性作出承诺。因使用本站内容而产生的任何直接或间接损失，本站不承担责任。
            </p>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/"
            className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            返回首页
          </Link>
          <Link
            to="/list/film"
            className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-background-300 text-sm font-medium text-foreground-800 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            浏览视频库
          </Link>
        </div>
      </section>
    </div>
  );
}